import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../middleware/auth0.service';
import {
  createStripeCustomer,
  RegisterShopOwner,
} from '../../middleware/registration_service/createRequest';
import { updateShopOwnerUserId } from '../../middleware/registration_service/updateRequest';
import { StripeCustomer } from '../../types/stripePayments';
import headerLogo from '../../images/header-logo.png';
import {
  darkPageBg,
  darkFormContainerSx,
  darkTextFieldSx,
  darkSelectSx,
  darkSelectMenuProps,
  darkButtonSx,
} from '../components/darkFormStyles';

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Dr', 'Prof'];

const ShopOwnerRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleTitleChange = (e: SelectChangeEvent) => {
    setForm({ ...form, title: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    let customerId: string | undefined;

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (
      !form.title ||
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      !form.password
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      // 1. Create Stripe customer
      try {
        const response = await createStripeCustomer({
          email: form.email,
          name: `${form.firstName} ${form.lastName}`,
        });
        const stripeCustomer: StripeCustomer = response.data;
        customerId = stripeCustomer.id;
        localStorage.setItem('reg_customer_id', stripeCustomer.id);
      } catch (stripeErr) {
        console.warn('Could not create Stripe customer:', stripeErr);
        setError('Failed to create Stripe customer. Please try again.');
        setLoading(false);
        return;
      }

      // 2. Register Shop Owner entity in backend
      const shopOwnerData = {
        customerId: customerId,
        title: form.title,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        subscriptionStatus: 'Active',
        shopSubscriptionId: 1,
      };
      const shopOwnerRes = await RegisterShopOwner(shopOwnerData);
      const shopOwnerId: bigint = shopOwnerRes.data.id;

      // Store shop owner id
      localStorage.setItem('reg_partner_id', shopOwnerId.toString());
      localStorage.setItem('reg_shop_owner_id', shopOwnerId.toString());

      // 3. Sign up in Auth0
      auth.signup(
        {
          email: form.email,
          password: form.password,
          connection: 'Username-Password-Authentication',
          userMetadata: {
            firstName: form.firstName,
            lastName: form.lastName,
          },
        },
        async (err: any, result: any) => {
          if (err) {
            setError(
              err.code === 'invalid_signup'
                ? 'This email already exists. Please log in.'
                : err.code === 'invalid_password'
                ? 'Password is not strong enough.'
                : err.description || err.code || 'Registration failed.'
            );
            setLoading(false);
            return;
          }

          const auth0UserId = result.Id || result._id || result.id || '';
          localStorage.setItem('reg_auth0_user_id', String(auth0UserId));

          // 4. Update Shop Owner with Auth0 User Id (best-effort)
          try {
            await updateShopOwnerUserId(shopOwnerId, String(auth0UserId));
          } catch (updateErr) {
            setError(
              'Failed to update shop owner with Auth0 User ID. Please try again.'
            );
          }

          // Store owner details for pre-fill in ShopBarberRegistration
          localStorage.setItem('reg_owner_email', form.email);
          localStorage.setItem('reg_owner_firstName', form.firstName);
          localStorage.setItem('reg_owner_lastName', form.lastName);
          localStorage.setItem('reg_owner_title', form.title);
          localStorage.setItem('reg_owner_phone', form.phoneNumber);

          setLoading(false);
          navigate('/register/shop');
        }
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'An error occurred during registration. Please try again.'
      );
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: darkPageBg, minHeight: '100vh', py: 8 }}>
      <Container
        component="main"
        maxWidth="xs"
        sx={{ ...darkFormContainerSx, mt: 4, mb: 4, p: 4 }}
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={headerLogo}
            alt="Barbers Limited"
            sx={{ width: 88, height: 88, objectFit: 'contain', mb: 1 }}
          />
          <Typography
            component="h1"
            variant="h5"
            fontWeight="bold"
            sx={{ color: '#fff' }}
          >
            Shop Owner Registration
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 1, textAlign: 'center', color: 'rgba(255,255,255,0.65)' }}
          >
            Step 1 of 3 — Register your account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 3, width: '100%' }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required sx={darkSelectSx}>
                  <InputLabel id="title-label">Title</InputLabel>
                  <Select
                    labelId="title-label"
                    id="title"
                    value={form.title}
                    label="Title"
                    onChange={handleTitleChange}
                    MenuProps={darkSelectMenuProps}
                  >
                    {TITLE_OPTIONS.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  autoFocus
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5, ...darkButtonSx }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Continue to Shop Details'
              )}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ShopOwnerRegistration;
